import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription, fromEvent } from 'rxjs';
import { startWith } from 'rxjs';
import { AppLoginService } from '../app-login.service';
import { LocalService } from '@app/@core/services/local.service';

@Component({
  standalone: false,
  selector: 'app-set-new-password',
  templateUrl: './set-new-password.component.html',
  styleUrls: ['./set-new-password.component.scss']
})
export class SetNewPasswordComponent implements OnInit, OnDestroy {
  passwordForm: FormGroup;
  showPassword = false;
  showConfirmPassword = false;
  allValidationsPass = false;
  showValidations = false;
  userEmail = ''
 
  isMobile = false;
  logoPath = 'assets/images/edu-logo.svg';
  isSetNewPasswordValid : boolean = true
  resizeObservable$: Subscription;
  
  validations = {
    minLength: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false,
    noPersonalInfo: true, 
    alphanumeric: false,
    noSpaces:false,
    noDictionaryWord: true 
  };

  constructor(private readonly fb: FormBuilder, private readonly router:Router, private appLoginService: AppLoginService , private sessionStorage: LocalService,) {
    this.passwordForm = this.fb.group({
      password: ['', [Validators.required]],
      confirmPassword: ['', [Validators.required]]
    }, {
      validators: this.passwordMatchValidator
    });
  }

  ngOnInit(): void {
    this.userEmail = this.sessionStorage.getItem('email' )
    this.setupResponsiveImage();
    const passwordControl = this.passwordForm.get('password')
    const confirmPasswordControl = this.passwordForm.get('confirmPassword')
    confirmPasswordControl.disable()
    passwordControl.valueChanges.subscribe(()=>{
      if(passwordControl.valid || passwordControl.value === ''){
      this.isSetNewPasswordValid = true
      confirmPasswordControl?.enable({emitEvent: false})
    }
    })
  }
  
  ngOnDestroy(): void {
    
    if (this.resizeObservable$) {
      this.resizeObservable$.unsubscribe();
    }
  }
  
  setupResponsiveImage(): void {
    this.resizeObservable$ = fromEvent(window, 'resize').pipe(startWith({} as Event))
      .subscribe(() => {
        this.isMobile = window.innerWidth < 748;
        this.updateLogoPath();
      });
    
    
    this.isMobile = window.innerWidth < 748;
    this.updateLogoPath();
  }
  
  updateLogoPath(): void {
    this.logoPath = this.isMobile 
      ? 'assets/images/cardy-ai/mobile-logo.svg'
      : 'assets/images/edu-logo.svg';
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  checkPasswordValidation(): void {
    const password = this.passwordForm.get('password')?.value;
    
    if (!password) {
      this.resetValidations();
      return;
    }

    // Check minimum length (8 characters)
    this.validations.minLength = password.length >= 8;
    
    // Check uppercase letter
    this.validations.uppercase = /[A-Z]/.test(password);
    
    // Check lowercase letter
    this.validations.lowercase = /[a-z]/.test(password);
    
    // Check for number
    this.validations.number = /\D/.test(password);
    
    // Check for special character
    this.validations.specialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
    
    // Check if alphanumeric plus special chars (all valid characters)
    this.validations.alphanumeric = /^[A-Za-z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+$/.test(password);

    // Check if there is no spaces
    this.validations.noSpaces = /^[A-Za-z0-9!@#$%^&*()_+\=\[\]{};':"\\|,.<>/?]+$/.test(password);
    
    // Check if all validations pass
    this.allValidationsPass = Object.values(this.validations).every(v => v === true);
    

    if (this.allValidationsPass) {
      this.isSetNewPasswordValid = false
      this.passwordForm.get('password')?.setErrors(null);
    } else {
      this.passwordForm.get('password')?.setErrors({ invalidPassword: true });
    }
  }

  resetValidations(): void {
    this.validations = {
      minLength: false,
      uppercase: false,
      lowercase: false,
      number: false,
      specialChar: false,
      noPersonalInfo: true,
      alphanumeric: false,
      noSpaces:false,
      noDictionaryWord: true
    };
    this.allValidationsPass = false;
  }

  passwordMatchValidator(form: FormGroup): { [key: string]: boolean } | null {
    const password = form.get('password')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;
    
    
    if (password && confirmPassword) {
      return password === confirmPassword ? null : { passwordMismatch: true };
    }
    return null;
  }

 onSubmit(): void {
  if (this.passwordForm.valid && this.allValidationsPass) {
    
    // Prepare API request data - change token to userId
    const resetPasswordData = {
      userId: this.userEmail, // Change from token to userId
      newPassword: this.passwordForm.get('password')?.value
    };

    // Call the reset password API
    this.appLoginService.resetPassword(resetPasswordData).subscribe({
      next: (response) => {
        console.log('Reset password API response:', response);
        
        // Navigate to success popup on API success
        this.router.navigate(['app-login/password-success-popup']);
      },
      error: (error) => {
        console.error('Reset password API error:', error);
        // You can add error handling here if needed later
      }
    });
  }
}
}
